import { segmentsStore } from '@/stores/segmentsStore'
import { documentStore } from '@/stores/documentStore'

import { SegmentEditorState } from '@/models/SegmentEditorState'
import { MergeState } from '@/models/MergeState';
import { SegmentEditorItem } from '@/models/SegmentEditorItem';
import { TranslationSegment } from '@/models/TranslationSegment';
import { SegmentEditorItemType } from '../SegmentEditorItemType';
import { DocumentType } from '@/models/DocumentType';
import { getMergeKey } from '@/models/segmentMergeKey';

import { segmentModificationService } from '@/services/translationSegment/segmentModificationService';
import { segmentTextLogicService } from '@/services/translationSegment/segmentTextLogicService';

export class translationSegmentService {
    private static instance: translationSegmentService;

    segmentsStore: any
    documentStore: any
    segmentModificationService: segmentModificationService;
    segmentTextLogicService: segmentTextLogicService;

    private constructor() {
        this.segmentsStore = segmentsStore();
        this.documentStore = documentStore();
        this.segmentModificationService = segmentModificationService.getInstance();
        this.segmentTextLogicService = segmentTextLogicService.getInstance();
    }

    public static getInstance(): translationSegmentService {
        if (!translationSegmentService.instance) {
            translationSegmentService.instance = new translationSegmentService();
        }
        return translationSegmentService.instance;
    }

    setFocusToSegment(segmentId: number) {
        this.segmentsStore.setCurrentSegmentId(segmentId);
    }

    showMergeButton(): boolean {
        return this.segmentsStore.segmentEditorState === SegmentEditorState.Translate;
    }

    private isDocxDocument(): boolean {
        return this.documentStore.documentType === DocumentType.Docx;
    }

    private hasValidNextSegment(segmentId: number): boolean {
        const index = this.segmentsStore.getSegmentPosition(segmentId);
        if (index === -1) return false;

        const next = this.segmentsStore.segmentsArray[index + 1];

        if (!next || next.segmentType !== SegmentEditorItemType.TranslationSegment) {
            return false;
        }

        return true;
    }

    isMergeDisabled(segmentItem: SegmentEditorItem): boolean {
        if (!this.hasValidNextSegment(segmentItem.itemId)) {
            return true;
        }

        if (this.isDocxDocument()) {
            return false;
        }

        return this.hasDifferentMergeGroupNext(segmentItem.itemId);
    }

    private hasDifferentMergeGroupNext(segmentId: number): boolean {
        const index = this.segmentsStore.getSegmentPosition(segmentId);
        if (index === -1) return false;

        const current = this.segmentsStore.segmentsArray[index];
        const next = this.segmentsStore.segmentsArray[index + 1];

        if (!next || next.segmentType !== SegmentEditorItemType.TranslationSegment) {
            return false;
        }

        const currentKey = getMergeKey(current.data);
        const nextKey = getMergeKey(next.data);

        return currentKey !== nextKey;
    }

    mergeIsPossible(segmentItem: SegmentEditorItem): boolean {
        if (!this.showMergeButton()) {
            return false;
        }

        return !this.isMergeDisabled(segmentItem);
    }

    canMergeWithNextSegment(segmentItem: SegmentEditorItem): boolean {
        const segmentIndex = this.segmentsStore.getSegmentPosition(segmentItem.itemId);
        const nextSegment = this.segmentsStore.segmentsArray[segmentIndex + 1];

        if (!nextSegment) {
            return false;
        }

        if (nextSegment.segmentType !== SegmentEditorItemType.TranslationSegment) {
            return false;
        }

        if (this.isDocxDocument()) {
            return true;
        }

        const currentKey = getMergeKey(segmentItem.data);
        const nextKey = getMergeKey(nextSegment.data);

        return currentKey === nextKey;
    }

    splitIsPossible(segmentItem: SegmentEditorItem) {
        return segmentItem.data.mergeState == MergeState.MergeResult &&
            this.segmentsStore.segmentEditorState == SegmentEditorState.Translate;
    }

    async copy(segmentId: number) {
        this.segmentsStore.setCurrentSegmentId(segmentId);
        await this.copyCurrentSegment();
    }

    async copyCurrentSegment() {
        const segmentItem = this.segmentsStore.getCurrentSegmentEditorItem();
        await this.segmentModificationService.copyAction(segmentItem.data);
    }

    async translate(segmentId: number) {
        this.segmentsStore.setCurrentSegmentId(segmentId);
        await this.translateCurrentSegment();
    }

    async translateCurrentSegment() {
        const segmentItem = this.segmentsStore.getCurrentSegmentEditorItem() as SegmentEditorItem;

        if (this.segmentsStore.translationConfiguration == null) {
            throw new Error("Translation configuration is not set");
        }

        segmentItem.loading = true;
        await this.segmentModificationService.translateAction(segmentItem.data, this.segmentsStore.translationConfiguration);

        segmentItem.loading = false;
    }

    async reloadTranslations(segmentId: number) {
        this.segmentsStore.setCurrentSegmentId(segmentId);
        await this.reloadCurrentSegmentTranslations();
    }

    async reloadCurrentSegmentTranslations() {
        const segmentItem = this.segmentsStore.getCurrentSegmentEditorItem() as SegmentEditorItem;

        if (this.segmentsStore.translationConfiguration == null) {
            throw new Error("Translation configuration is not set");
        }

        segmentItem.loading = true;
        await this.segmentModificationService.reloadSegmentTranslationsAction(segmentItem.data, this.segmentsStore.translationConfiguration);

        segmentItem.loading = false;
    }

    toogleCurrentSegmentVariantsPopup() {
        const segmentItem = this.segmentsStore.getCurrentSegmentEditorItem() as SegmentEditorItem;

        segmentItem.variantsPopup = !segmentItem.variantsPopup;
    }

    async addTranslation(segmentId: number) {
        this.segmentsStore.setCurrentSegmentId(segmentId);
        await this.addCurrentSegmentTranslation();
    }

    async addCurrentSegmentTranslation() {
        const segmentItem = this.segmentsStore.getCurrentSegmentEditorItem();

        if (this.segmentsStore.translationConfiguration == null) {
            throw new Error("Translation configuration is not set");
        }

        segmentItem.loading = true;
        await this.segmentModificationService.addTranslationAction(segmentItem.data, this.segmentsStore.translationConfiguration);
        segmentItem.loading = false;
    }

    currentTargetIsEmpty() {
        const segmentItem = this.segmentsStore.getCurrentSegmentEditorItem() as SegmentEditorItem;
        return this.targetIsEmpty(segmentItem.data);
    }

    targetIsEmpty(segment: TranslationSegment) {
        const segmentText = segment.draftData.text.trim()
        return segmentText == '';
    }

    translationCouldBeAdded() {
        const segmentItem = this.segmentsStore.getCurrentSegmentEditorItem() as SegmentEditorItem;
        const translationSegment = segmentItem.data;

        const couldBeAdded = !this.targetIsEmpty(translationSegment) &&
            !this.segmentTextLogicService.translationReady(translationSegment) &&
            this.segmentTextLogicService.segmentStatusIsOkay(translationSegment);

        return couldBeAdded;
    }

    translationCouldBeUpdated() {
        const segmentItem = this.segmentsStore.getCurrentSegmentEditorItem() as SegmentEditorItem;
        const translationSegment = segmentItem.data;

        const couldBeUpdated =
            this.segmentTextLogicService.segmentStatusIsOkay(translationSegment) &&
            this.segmentTextLogicService.perfectMatch(translationSegment) &&
            this.segmentTextLogicService.selectedOwnTranslation(translationSegment) &&
            (
                !this.segmentTextLogicService.drftTextEqalTranslationText(translationSegment) ||
                !this.segmentTextLogicService.drftStyleEqalTranslationStyle(translationSegment)
            )

        return couldBeUpdated;
    }

    async updateTranslation(segmentId: number) {
        this.segmentsStore.setCurrentSegmentId(segmentId);
        await this.updateCurrentSegmentTranslation();
    }

    async updateCurrentSegmentTranslation() {
        if (this.segmentsStore.translationConfiguration == null) {
            throw new Error("Translation configuration is not set");
        }

        const segmentItem = this.segmentsStore.getCurrentSegmentEditorItem();
        segmentItem.loading = true;
        await this.segmentModificationService.updateTranslationAction(segmentItem.data, this.segmentsStore.translationConfiguration);
        segmentItem.loading = false;
    }

    async cleanTranslationSegment(segmentId: number) {
        this.segmentsStore.setCurrentSegmentId(segmentId);
        await this.cleanCurrentSegmentTranslationSegment();
    }

    async cleanCurrentSegmentTranslationSegment() {
        const segmentItem = this.segmentsStore.getCurrentSegmentEditorItem();

        await this.segmentModificationService.clearTranslationAction(segmentItem.data);
    }

    async removeStylesInCurrentSrgment() {
        const segmentItem = this.segmentsStore.getCurrentSegmentEditorItem();
        await this.segmentModificationService.clearSegmnetStylesAction(segmentItem.data);
    }

}